import React from 'react';
import { cn } from '@/lib/utils';

interface PremiumBackgroundProps {
  /** 
   * Variant controls gradient intensity:
   * - hero: Full gradient mesh with animated orbs
   * - section: Lighter gradient for content sections
   * - page: Gradient for page headers (Explore, About)
   */
  variant?: 'hero' | 'section' | 'page';
  /** Show animated blur orbs for additional depth */
  showOrbs?: boolean;
  /** Additional class names */
  className?: string;
  /** Children to render on top of background */
  children?: React.ReactNode;
}

const PremiumBackground: React.FC<PremiumBackgroundProps> = ({
  variant = 'hero',
  showOrbs = true,
  className,
  children
}) => {
  const meshClass = {
    hero: 'premium-gradient-mesh',
    section: 'premium-gradient-mesh-light',
    page: 'premium-gradient-mesh-page'
  }[variant];

  // Orb configurations per variant
  const orbConfigs = {
    hero: [
      { className: 'w-48 md:w-80 h-48 md:h-80 -top-10 left-1/4', delay: '0s' },
      { className: 'w-56 md:w-96 h-56 md:h-96 top-10 right-1/4', delay: '2s' },
      { className: 'w-40 md:w-72 h-40 md:h-72 bottom-10 left-1/3', delay: '4s' }
    ],
    section: [
      { className: 'w-32 md:w-64 h-32 md:h-64 top-0 left-1/5', delay: '0s' },
      { className: 'w-40 md:w-72 h-40 md:h-72 bottom-0 right-1/4', delay: '3s' }
    ],
    page: [
      { className: 'w-48 md:w-72 h-48 md:h-72 -top-10 left-1/4', delay: '0s' },
      { className: 'w-56 md:w-80 h-56 md:h-80 top-10 right-1/4', delay: '2s' },
      { className: 'w-96 h-96 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2', delay: '4s' }
    ]
  };

  const orbs = orbConfigs[variant];

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Base gradient mesh */}
      <div className={cn('absolute inset-0', meshClass)} />
      
      {/* Animated blur orbs for depth */}
      {showOrbs && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {orbs.map((orb, index) => (
            <div
              key={index}
              className={cn('premium-orb premium-orb-animated', orb.className)}
              style={{ animationDelay: orb.delay }}
            />
          ))}
        </div>
      )}
      
      {/* Content */}
      {children && (
        <div className="relative z-10">
          {children}
        </div>
      )}
    </div>
  );
};

export default PremiumBackground;
